import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { onAuthStateChanged, onIdTokenChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { createUserProfile, getUserPermissions } from '../services/userService';
import { isPlatformAdmin } from '../utils/internalCompany';
import { markSessionHint, clearSessionHint } from '../utils/sessionHint';
import { reconcileCompanyClaim } from '../services/authClaims';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  // ── EMAIL VERIFIED, AS STATE (the account gate, 2026-10-17) ──────────────
  // `user.emailVerified` lives on the Firebase User object, and reload() flips
  // it IN PLACE — the same object, so nothing re-renders and the router never
  // learns the address was confirmed. This mirrors it into state from every
  // token change (sign-in, sign-out, refresh) and from refreshEmailVerified()
  // below, which is what the "check your email" screen polls.
  const [emailVerified, setEmailVerified] = useState(false);

  useEffect(() => onIdTokenChanged(auth, (u) => setEmailVerified(!!(u && u.emailVerified))), []);

  // Ask Firebase whether the address has been confirmed since this session
  // loaded — the link is usually opened in another tab, or on a phone. On a yes
  // it also forces a FRESH ID token, because the server's gate reads
  // `email_verified` off the token, and a token minted before the click still
  // says false for up to an hour.
  const refreshEmailVerified = useCallback(async () => {
    const u = auth.currentUser;
    if (!u) return false;
    await u.reload();
    if (u.emailVerified) await u.getIdToken(true);
    setEmailVerified(!!u.emailVerified);
    return !!u.emailVerified;
  }, []);

  useEffect(() => {
    // Live listener on the current user's Firestore doc so name/role changes
    // (from My Profile or an owner editing in Settings) propagate instantly to
    // the sidebar and everywhere else without a refresh.
    let unsubProfile = null;
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      // Tear down any previous profile listener before re-subscribing.
      if (unsubProfile) { unsubProfile(); unsubProfile = null; }

      // Keep the synchronous "/" fast-path hint honest. The login and logout
      // handlers set and clear it directly, but this is the only place that
      // sees EVERY transition, so it is what makes the hint self-correcting:
      // a user who was already signed in before the hint existed gets one
      // written on their next load, and a session that expired, was revoked,
      // or was ended in another tab gets the stale hint removed here rather
      // than costing a pointless spinner on every future visit to "/".
      // Purely a rendering hint — see utils/sessionHint.js.
      if (currentUser) markSessionHint(); else clearSessionHint();

      if (currentUser) {
        // Same tick as setUser, so no render ever pairs a signed-in user with a
        // stale verified flag — onIdTokenChanged may fire before or after this.
        setEmailVerified(!!currentUser.emailVerified);
        setUser(currentUser);
        const ref = doc(db, 'users', currentUser.uid);
        // METADATA CHANGES ARE PART OF THE SIGNAL HERE, not noise — see the
        // fromCache reasoning below. Without them a snapshot that goes from
        // "cache says absent" to "SERVER says absent" carries no data change,
        // so the SDK suppresses it and a genuine first-time user who happened
        // to start offline would never get their profile created.
        unsubProfile = onSnapshot(
          ref,
          { includeMetadataChanges: true },
          async (snap) => {
            // ── THE ONBOARDING RACE, AND WHY fromCache IS THE FIX ──────────
            // `snap.exists() === false` has TWO completely different meanings
            // and they are indistinguishable by exists() alone:
            //
            //   fromCache: false → the SERVER says this user has no profile.
            //                      A genuine first sign-in. Create the stub.
            //   fromCache: true  → the LOCAL CACHE has never seen the document.
            //                      Says nothing about whether it exists. The
            //                      app uses the default memory cache, so this
            //                      is what EVERY cold load looks like until
            //                      Firestore answers — and what a flaky
            //                      connection looks like for as long as it
            //                      lasts.
            //
            // Trusting the second one is what routed onboarded users into
            // onboarding. createUserProfile() writes { companyId: null,
            // role: 'salesman' }, Firestore applies that write to the local
            // cache IMMEDIATELY (it is queued, not sent), the listener re-fires
            // with the stub, and every route gate then reads a signed-in user
            // with no company and sends them to "Let's get your company set
            // up." Measured: an onboarded owner deep-linked to /tasks with
            // Firestore unreachable landed on /onboarding 340ms after boot.
            //
            // The stub write is rejected by firestore.rules when the network
            // returns (it touches companyId + role), so the real profile
            // survives — but that is the rules catching a write the app should
            // never have attempted, not a design.
            const fromServer = !snap.metadata.fromCache;

            if (snap.exists()) {
              setUserProfile(snap.data());
              setLoading(false);
              return;
            }

            // Document missing and only the CACHE says so. That is not an
            // answer — stay in the loading state and wait for the server.
            // Nothing routes off a state this callback hasn't settled.
            if (!fromServer) return;

            // Server-confirmed absent: first-time user. This listener re-fires
            // once the profile is written.
            await createUserProfile(currentUser.uid, currentUser.email);
          },
          (err) => {
            console.error('User profile listener error:', err);
            setLoading(false);
          }
        );
      } else {
        setEmailVerified(false);
        setUser(null);
        setUserProfile(null);
        setLoading(false);
      }
    });

    return () => {
      if (unsubProfile) unsubProfile();
      unsubscribe();
    };
  }, []);

  // ── KEEPING THE TOKEN'S COMPANY CLAIM HONEST ──────────────────────────────
  //
  // storage.rules authorizes on `request.auth.token.companyId`, a custom claim
  // a Firestore trigger derives from users/{uid}.companyId. A claim only
  // appears in a token minted AFTER it was set, and Firebase caches an ID token
  // for about an hour — so without this, two real situations silently break:
  //
  //   • ANY SESSION THAT WAS OPEN WHEN THE CLAIM SHIPPED. Their token predates
  //     the claim entirely. Left alone they would get up to an hour of refused
  //     uploads and downloads with no explanation, then start working by
  //     themselves. That is the confusing half-broken window this exists to
  //     close, and it closes on the next page load rather than on a sign-out.
  //   • A COMPANY CHANGE MADE ANYWHERE ELSE — the server, another tab, an
  //     owner's edit. The profile listener above already picks the new value up
  //     live; this makes the TOKEN follow it.
  //
  // It refreshes only when the token actually disagrees with the profile, so
  // the overwhelmingly common case (they agree) costs one cached read and no
  // network. Failure is bounded and logged, never thrown: a claim that cannot
  // be minted must surface as a refused file operation with a console
  // explanation, not as an app that will not load.
  useEffect(() => {
    if (!user || !userProfile) return;
    let cancelled = false;
    (async () => {
      try {
        const ok = await reconcileCompanyClaim(user, userProfile.companyId);
        if (!ok && !cancelled && userProfile.companyId) {
          console.warn(
            '[AuthContext] the token for this session still has no company claim. File uploads and ' +
            'downloads will be refused until it does — sign out and back in if it persists.'
          );
        }
      } catch (err) {
        console.error('[AuthContext] company claim reconcile failed:', err);
      }
    })();
    return () => { cancelled = true; };
    // Keyed on the IDENTITY and the company, not the whole profile object: the
    // profile listener re-fires on every unrelated field change (theme, widget
    // layout, notification prefs) and re-running a token refresh for those
    // would be pure waste.
  }, [user, userProfile?.companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = {
    user,
    userProfile,
    userPerms: getUserPermissions(userProfile),
    // BoneFlow's own operator flag (roadmap 5.10), read straight off the user
    // doc. Deliberately kept OUT of userPerms: those are per-company
    // permissions an owner can grant; this one is platform-wide, set by hand
    // server-side, and firestore.rules blocks every client write to it.
    platformAdmin: isPlatformAdmin(userProfile),
    emailVerified,
    refreshEmailVerified,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {/* Children render from the FIRST TICK, including while `loading` is
          still true. This used to be `{!loading && children}`, which held the
          entire app — App.js's router included — behind onAuthStateChanged, so
          the public landing page sat behind an auth round trip that no
          signed-out visitor has any use for. Optimistically rendering
          <LandingPage /> at "/" is pointless while this gate exists.

          Every consumer that needs a settled answer already gates on `loading`
          itself: ProtectedRoute, LoginRoute and OnboardingRoute show
          LoadingScreen, and SignupPage suppresses its signed-in redirect until
          `!authLoading`. The providers below are null-safe too — CompanyProvider
          skips its fetch while userProfile is null and re-runs on companyId,
          ThemeProvider bails until userProfile.uid exists. The public pages
          (/, /signup, /action) now paint without waiting. */}
      {children}
    </AuthContext.Provider>
  );
}